import { QueryObserverResult } from '@tanstack/react-query';
import classes from 'classnames';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import Close from 'assets/icons/close.svg?react';
import PDF from 'assets/icons/pdf.svg?react';
import VectorIcon from 'assets/icons/vector.svg?react';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import styles from './DocumentModal.module.scss';

export type Props = {
  onClose?: (event: React.MouseEvent<Element, MouseEvent> | MouseEvent) => void;
  children?: ReactNode;
  backdropClickClosesModal?: boolean;
  options?: ModalOptions;
  className?: string;
  classNameOverlay?: string;
  classNameModalContent?: string;
  containerClassName?: string;
  dataTest?: string;
  pdfData?: Blob;
  fetchPdf?: () => Promise<QueryObserverResult<
    string | Blob | null,
    Error
  > | null>;
};

export type ModalOptions = {
  header?: {
    title?: string;
    closeButton?: boolean;
    banner?: string;
  };
  footer?: {
    acceptTitle?: string;
    acceptPrint?: string;
    onAccept?: (
      event: React.MouseEvent<Element, MouseEvent> | MouseEvent,
    ) => void;
    print?: boolean;
  };
  onClose?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | MouseEvent,
  ) => void;
  children?: ReactNode;
  backdropClose?: boolean;
  className?: string;
};

function DocumentModal({
  onClose,
  children,
  backdropClickClosesModal = true,
  dataTest = 'documentModal',
  options,
  className,
  classNameModalContent,
  classNameOverlay,
  containerClassName,
  pdfData,
  fetchPdf,
}: Props) {
  const [loadingPdf, setLoadingPdf] = useState(false);
  const documentRef = useRef<HTMLDivElement>(null);
  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const [isScrolled, setScrolled] = useState(true);

  const handleAccept = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      if (!onClose) return;
      onClose(e);
      if (options?.footer?.onAccept) {
        options.footer.onAccept(e);
      }
    },
    [onClose, options?.footer],
  );

  const print = useCallback(() => {
    const printWindow = printFrameRef.current?.contentWindow;

    if (printWindow && documentRef.current) {
      printWindow.document.open();
      printWindow.document.write(documentRef.current.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  }, []);

  const openPDF = useCallback(async () => {
    try {
      setLoadingPdf(true);

      const dataPdf = pdfData || (await fetchPdf?.())?.data;

      if (!dataPdf || !(dataPdf instanceof Blob)) {
        return;
      }
      const url = window.URL.createObjectURL(dataPdf);
      window.open(url, '_blank');
    } catch {
      print(); // open print modal in case pdf is not available
    } finally {
      setLoadingPdf(false);
    }
  }, [pdfData]);

  const handleScroll = () => {
    if (documentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = documentRef.current;
      if (scrollTop + clientHeight + 1 >= scrollHeight) {
        setScrolled(false);
      }
    }
  };

  return (
    <Modal
      dataTest={`${dataTest}-modal`}
      backdropClose={backdropClickClosesModal}
      className={classes(className, styles.card)}
      classNameOverlay={classNameOverlay}
      contentClassName={classNameModalContent}
      containerClassName={containerClassName}
    >
      {options?.header && (
        <div className={styles.header}>
          <div
            className={styles['header-title']}
            data-test={`${dataTest}-title`}
          >
            {options?.header.title}
          </div>
          <div className={styles['window-options']}>
            <Close
              className={styles.close}
              onClick={onClose}
              data-test={`${dataTest}-close-icon`}
            />
          </div>
        </div>
      )}
      <div
        ref={documentRef}
        onScroll={() => handleScroll()}
        className={styles.content}
        data-test={`${dataTest}-scrollable-content`}
      >
        {options?.header?.banner && (
          <div className={styles.banner}>
            <VectorIcon data-test={`${dataTest}-vector-icon`} />
            <span data-test={`${dataTest}-banner-text`}>
              {options.header.banner}
            </span>
          </div>
        )}
        {children}
      </div>
      {options?.footer && (
        <div className={styles.footer}>
          <Button
            dataTest="modal-button"
            size="l"
            className={styles.button}
            onClick={handleAccept}
            disabled={isScrolled}
          >
            {options.footer.acceptTitle}
          </Button>
          {options?.footer?.print && (
            <button
              type="button"
              className={styles['print-wrapper']}
              data-test={`${dataTest}-print-button`}
              disabled={loadingPdf}
              onClick={pdfData || fetchPdf ? openPDF : print}
            >
              <PDF
                className={classes(styles.pdf, loadingPdf && styles.disabled)}
                data-test={`${dataTest}-pdf-icon`}
              />
              <span
                className={classes(styles.print, loadingPdf && styles.disabled)}
                data-test={`${dataTest}-print-text`}
              >
                {options.footer.acceptPrint}
              </span>
            </button>
          )}
        </div>
      )}
      <iframe
        title="Print frame"
        ref={printFrameRef}
        className={styles['print-frame']}
      />
    </Modal>
  );
}

export default DocumentModal;
